/**
 * Breakdown of the selected basis across one dimension.
 *
 * Suppression: the API returns value=null / suppressed=true for any group under
 * the minimum size. Those rows STAY — the group is real and hiding it would
 * distort the shape of the table — but the count renders as a dash with the
 * rule attached. A suppressed cell is never coerced to 0, and never contributes
 * a bar.
 */
import React from 'react';
import { cn } from '@/lib/utils';
import { PanelShell, PanelSkeleton, PanelUnavailable } from './PanelShell';
import {
  ACCENT,
  Def,
  GROUP_BY_OPTIONS,
  fmtNum,
  fmtPct,
  type DefLookup,
  type WorkforceBreakdown,
  type WorkforceGroupBy,
} from './apiContract';

interface BreakdownPanelProps {
  breakdown: WorkforceBreakdown | null;
  groupBy: WorkforceGroupBy;
  onGroupBy: (g: WorkforceGroupBy) => void;
  periodLabel: string;
  basisNote: string;
  loading: boolean;
  def: DefLookup;
}

const SUPPRESSION_NOTE =
  'Withheld: fewer than 5 in this group. A cell that small identifies individuals, so the count is withheld while the group itself stays visible. It is not zero.';

export const BreakdownPanel: React.FC<BreakdownPanelProps> = ({
  breakdown,
  groupBy,
  onGroupBy,
  periodLabel,
  basisNote,
  loading,
  def,
}) => {
  const rows = breakdown?.rows ?? [];
  const max = Math.max(0, ...rows.map((r) => r.value ?? 0));
  const groupLabel = GROUP_BY_OPTIONS.find((g) => g.id === groupBy)?.label ?? groupBy;
  const suppressed = breakdown?.suppressed_rows ?? 0;

  return (
    <PanelShell
      title="Breakdown"
      defKey="breakdown"
      def={def}
      window={`${basisNote} at ${periodLabel}. Follows both the basis toggle and the period picker.`}
      right={
        <div className="flex flex-wrap items-center gap-1.5">
          {GROUP_BY_OPTIONS.map((g) => (
            <button
              key={g.id}
              type="button"
              aria-pressed={groupBy === g.id}
              onClick={() => onGroupBy(g.id)}
              className={cn(
                'rounded-full border px-2.5 py-0.5 text-[11px] transition-colors',
                groupBy === g.id
                  ? 'border-heritage-blue bg-heritage-blue text-white'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50',
              )}
            >
              {g.label}
            </button>
          ))}
        </div>
      }
      footnote={
        <>
          {breakdown?.note ? <span>{breakdown.note} </span> : null}
          {suppressed > 0 ? (
            <span className="text-amber-700">
              {suppressed} {suppressed === 1 ? 'group is' : 'groups are'} below the minimum group size
              of 5 and shown as a dash — withheld, not zero.
            </span>
          ) : null}
        </>
      }
    >
      {loading && !breakdown ? (
        <PanelSkeleton height={220} />
      ) : !breakdown ? (
        <PanelUnavailable what="The breakdown" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="border-b text-gray-500">
              <tr>
                <th className="px-2 py-1.5 text-left font-medium">
                  <span className="inline-flex items-center">
                    {groupLabel}
                    <Def def={def} k={`group_${groupBy}`} />
                  </span>
                </th>
                <th className="px-2 py-1.5 text-left font-medium" style={{ width: '38%' }}>
                  <span className="sr-only">Relative size</span>
                </th>
                <th className="px-2 py-1.5 text-right font-medium">
                  <span className="inline-flex items-center">
                    Count
                    <Def def={def} k="breakdown_value" />
                  </span>
                </th>
                <th className="px-2 py-1.5 text-right font-medium">
                  <span className="inline-flex items-center">
                    Share
                    <Def def={def} k="breakdown_share" />
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="max-w-[260px] truncate px-2 py-1.5 text-gray-700">{r.label}</td>
                  <td className="px-2 py-1.5">
                    <div className="relative h-[10px] overflow-hidden rounded-sm bg-gray-100">
                      {r.value !== null && r.value !== undefined && max > 0 ? (
                        <span
                          className="absolute inset-y-0 left-0 rounded-sm"
                          style={{ width: `${Math.max(0.8, (r.value / max) * 100)}%`, background: ACCENT }}
                        />
                      ) : null}
                    </div>
                  </td>
                  <td
                    className={cn(
                      'px-2 py-1.5 text-right tabular-nums',
                      r.suppressed || r.value === null || r.value === undefined
                        ? 'text-gray-400'
                        : 'font-medium text-gray-800',
                    )}
                  >
                    {r.value === null || r.value === undefined ? (
                      <span className="inline-flex items-center justify-end">
                        —
                        <Def def={def} k="suppression" text={SUPPRESSION_NOTE} />
                      </span>
                    ) : (
                      fmtNum(r.value)
                    )}
                  </td>
                  <td
                    className={cn(
                      'px-2 py-1.5 text-right tabular-nums',
                      r.share === null || r.share === undefined ? 'text-gray-400' : 'text-gray-600',
                    )}
                  >
                    {r.share === null || r.share === undefined ? '—' : fmtPct(r.share)}
                  </td>
                </tr>
              ))}
              {!rows.length ? (
                <tr>
                  <td colSpan={4} className="px-2 py-8 text-center text-gray-400">
                    No rows for this selection.
                  </td>
                </tr>
              ) : null}
            </tbody>
            {rows.length ? (
              <tfoot>
                <tr className="border-t-2">
                  <td className="px-2 py-1.5 font-semibold text-heritage-blue">Total</td>
                  <td />
                  <td className="px-2 py-1.5 text-right font-bold tabular-nums text-heritage-blue">
                    {fmtNum(breakdown.total)}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-gray-500">100.0%</td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      )}
    </PanelShell>
  );
};
